import { FC, useState } from "react"
import { Document } from "@/types"
import {
	FileText,
	Download,
	MoreHorizontal,
	Loader2,
	Tag,
	Eye,
} from "lucide-react"
import { cn } from "@/lib/utils"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import {
	useDocumentStages,
	DocumentStagesData,
} from "@/hooks/useDocumentStages"
import { isDocumentRelevant } from "@/utils/documentUtils"
import { DocumentSummary } from "./DocumentSummary"
import { UpdateRelevancyModal } from "./UpdateRelevancyModal"
import { DocumentViewerModal, isDocumentViewable } from "./DocumentViewerModal"
import { formatFileSize } from "@/utils/formatFileSize"
import { useDocumentDownload } from "@/hooks/useDocumentDownload"

interface RelevantDocumentCardProps {
	document: Document
	caseId: string
	onDelete: () => void
	onDeleteMultipleSelected: () => void
	isSelected?: boolean
	onSelect?: () => void
	index?: number
	stagesData?: DocumentStagesData // Optional prop for pre-fetched stages
}

const getDocumentTypeStyle = (file_extension: string) => {
	switch (file_extension.toLowerCase()) {
		case "pdf":
			return "text-primary bg-primary/10"
		case "docx":
			return "text-blue-500 bg-blue-50"
		case "xlsx":
			return "text-green-500 bg-green-50"
		default:
			return "text-muted-foreground bg-muted/10"
	}
}

export const RelevantDocumentCard: FC<RelevantDocumentCardProps> = ({
	document: documentData,
	caseId,
	onDelete,
	onDeleteMultipleSelected,
	isSelected,
	onSelect,
	index,
	stagesData: propStagesData,
}) => {
	// Use prop data if provided, otherwise fetch
	const {
		data: fetchedStagesData,
		isLoading,
		error,
	} = useDocumentStages(
		propStagesData ? undefined : caseId,
		propStagesData ? undefined : documentData.id
	)

	const stagesData = propStagesData || fetchedStagesData

	const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false)
	const [isUpdateRelevancyModalOpen, setIsUpdateRelevancyModalOpen] =
		useState(false)
	const [isViewerOpen, setIsViewerOpen] = useState(false)
	const { downloadDocument, isDownloading } = useDocumentDownload()

	if (!propStagesData && isLoading) {
		return (
			<div className="rounded-lg overflow-hidden border bg-card p-4 flex items-center justify-center min-h-[120px]">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
			</div>
		)
	}

	if (!propStagesData && error) {
		console.error(
			`[RelevantDocumentCard] Error loading document stages for ${documentData.id}:`,
			error
		)
		return null
	}

	if (!stagesData || !isDocumentRelevant(stagesData)) {
		return null
	}

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (onSelect && (event.key === "Enter" || event.key === " ")) {
			event.preventDefault() // Prevent default space bar scroll
			onSelect()
		}
	}

	const handleView = () => {
		setIsViewerOpen(true)
	}

	const handleDownload = () => {
		downloadDocument(caseId, documentData)
	}

	return (
		<>
			<DocumentViewerModal
				isOpen={isViewerOpen}
				onClose={() => setIsViewerOpen(false)}
				document={documentData}
				caseId={caseId}
			/>
			<div
				className={cn(
					"document-card rounded-lg overflow-hidden border cursor-pointer transition-colors duration-150 ease-in-out",
					"hover:-translate-y-1",
					isSelected ? "bg-red-50 border-red-300" : "bg-card hover:bg-muted/50",
					"self-start"
				)}
				style={{ animationDelay: `${(index || 0) * 150}ms` }}
				onClick={onSelect}
				{...(onSelect && {
					role: "button",
					tabIndex: 0,
					onKeyDown: handleKeyDown,
				})}
			>
				<div className="opacity-0 animate-fade-slide-in w-full">
					<div className="p-4">
						{/* --- Top Row Wrapper --- */}
						<div className="flex justify-between items-start mb-3">
							{/* File Type Badge */}
							<div
								className={cn(
									"inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg",
									getDocumentTypeStyle(documentData.file_extension)
								)}
							>
								<FileText className="h-4 w-4" aria-hidden="true" />
								<span className="text-sm font-medium">
									{stagesData?.stage1?.understood_file_type ||
										documentData.file_extension.toUpperCase()}
								</span>
							</div>
							{/* Actions Menu - Moved Here */}
							<div className="flex-shrink-0 min-w-[2rem]">
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 flex-shrink-0"
											aria-label={`Actions for ${documentData.name}`}
										>
											<MoreHorizontal className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem
											onSelect={handleView}
											disabled={
												!isDocumentViewable(documentData.file_extension)
											}
											onClick={(e) => e.stopPropagation()}
										>
											<Eye className="mr-2 h-4 w-4" />
											View
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={handleDownload}
											onClick={(e) => e.stopPropagation()}
											disabled={isDownloading}
										>
											<Download className="mr-2 h-4 w-4" />
											{isDownloading ? "Downloading..." : "Download"}
										</DropdownMenuItem>
										<DropdownMenuItem
											onSelect={() => setIsUpdateRelevancyModalOpen(true)}
											onClick={(e) => e.stopPropagation()}
										>
											<Tag className="mr-2 h-4 w-4" />
											Update Relevancy
										</DropdownMenuItem>
										<DropdownMenuItem
											className="text-destructive focus:text-destructive focus:bg-destructive/10"
											onSelect={onDelete}
											onClick={(e) => e.stopPropagation()}
										>
											Delete
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
						<div className="space-y-2">
							{/* Document Info - now with more vertical space */}
							<div>
								<h3
									className="text-lg font-medium line-clamp-2"
									title={documentData.name}
								>
									{documentData.name}
								</h3>
								{stagesData?.stage2?.four_line_summary && (
									<DocumentSummary
										stagesData={stagesData}
										preview="four_line"
									/>
								)}
							</div>
						</div>

						{/* Metadata Footer */}
						<div className="mt-3 pt-2 border-t text-xs text-muted-foreground">
							<span>{formatFileSize(documentData.size)}</span>
							<span className="mx-2">•</span>
							<span>
								Uploaded{" "}
								{new Date(documentData.created_at).toLocaleDateString()}
							</span>
						</div>
					</div>

					<UpdateRelevancyModal
						open={isUpdateRelevancyModalOpen}
						onOpenChange={setIsUpdateRelevancyModalOpen}
						documentId={documentData.id}
						caseId={caseId}
						documentName={documentData.name}
						currentIsRelevant={stagesData?.stage3?.is_relevant || null}
					/>
				</div>
			</div>
		</>
	)
}
